/**
 * The Chronology tab's model (1.O-1 deliverable 13), pure: a page's dates with their order rules,
 * the events it takes part in, its dated links, and an age. Every rule reads from this page's own
 * side; titles are as the GM wrote them; month and era names come from the compiled calendar.
 */
import { add, type CompiledCalendar, formatTimePoint, toParts } from '@codex/calendar';
import type {
  ConstraintV1,
  DatedPointV1,
  LinkBoundaryV1,
  LinkV1,
  PlacementV1,
  Precision,
  TickSpanV1,
} from '@codex/contracts';
import { placementDate } from './pill-model.js';
import {
  ANOTHER_DATE,
  ageAtDeathText,
  ageAtNowText,
  CONSTRAINT_STATUS_WORDS,
  cap,
  INVALID_POINT,
  INVALID_SPAN,
  lagText,
  NOW_UNSET,
  reasonText,
  roleWord,
  statusWord,
  UNKNOWN_DATE,
} from './words.js';

/**
 * A tick in the tab's one date form: the canonical form of the day (or other precision) holding
 * it, the era written only when it is not the calendar's default, as the date rows write theirs
 * ("1 Ember 1040", where `formatTick` writes "1 Ember 1040 AL").
 */
export function tickDate(
  calendar: CompiledCalendar,
  tick: number,
  precision: Precision = 'day',
): string | null {
  const parts = toParts(calendar, tick, precision);
  if (parts === null) return null;
  return formatTimePoint(calendar, {
    loParts: parts,
    hiParts: null,
    precision,
    qualifier: 'exact',
  });
}

const byOrder = (a: PlacementV1, b: PlacementV1) =>
  a.orderKey < b.orderKey ? -1 : a.orderKey > b.orderKey ? 1 : a.id < b.id ? -1 : 1;

// ----------------------------------------------------------------------------------- rules

/** A rule from one side: the words before the other side's title, and that side. */
export interface RuleParts {
  /** "3 days after the end of ", "Within ", "At the same time as ". */
  readonly prefix: string;
  /** The placement on the other side. */
  readonly otherId: string;
  /** Its title as written, or "another date" when it is not known. */
  readonly title: string;
}

/** `constraintText` in two parts, so the tab can link the other side's title. */
export function constraintParts(
  c: ConstraintV1,
  selfId: string,
  titles: ReadonlyMap<string, string>,
): RuleParts {
  const selfAfter = c.afterId === selfId;
  const otherId = selfAfter ? c.beforeId : c.afterId;
  const title = titles.get(otherId) ?? ANOTHER_DATE;
  const lag =
    c.lagUnit !== null && (c.minLagN !== null || c.maxLagN !== null)
      ? `${lagText({ min: c.minLagN, max: c.maxLagN, unit: c.lagUnit })} `
      : '';
  let prefix: string;
  if (c.relation === 'before') {
    prefix = selfAfter
      ? cap(`${lag}after the ${c.beforeEdge} of `)
      : cap(`${lag}before the ${c.afterEdge} of `);
  } else if (c.relation === 'during') prefix = selfAfter ? 'Within ' : 'Contains ';
  else prefix = 'At the same time as ';
  return { prefix, otherId, title };
}

/**
 * A rule in words from `selfId`'s side, the other side by `titles` (placement id to title):
 * "3 days after the end of The Siege of Vesk", "Before the start of …", "Within …",
 * "Contains …" (the container's side of `during`), "At the same time as …".
 */
export function constraintText(
  c: ConstraintV1,
  selfId: string,
  titles: ReadonlyMap<string, string>,
): string {
  const parts = constraintParts(c, selfId, titles);
  return `${parts.prefix}${parts.title}`;
}

/** A rule's status in words; null for an active rule. */
export function constraintStatusText(c: ConstraintV1): string | null {
  return CONSTRAINT_STATUS_WORDS[c.status] ?? null;
}

// ----------------------------------------------------------------------------------- dates

export interface RuleRow {
  readonly id: string;
  readonly constraint: ConstraintV1;
  readonly text: string;
  /** `text` before the other side's title. */
  readonly prefix: string;
  /** The other side's title as written ("another date" when unknown). */
  readonly otherTitle: string;
  /** The other side's page, to open; null for one of this page's own dates or an unknown one. */
  readonly otherDocId: string | null;
  readonly status: string | null;
}

export interface DateRow {
  readonly id: string;
  readonly placement: PlacementV1;
  /** "Happens", "Born", "Died", "Session", else the role. */
  readonly role: string;
  readonly label: string | null;
  /** "Planned", "Rumor", "Alternate", "Retired"; null for canon. */
  readonly badge: string | null;
  /** The date in its canonical form, or "Undated". */
  readonly date: string;
  /** No rank: undated and unconstrained. */
  readonly unplaced: boolean;
  /** "This date can't be placed on the calendar." with its reason, for invalid ticks. */
  readonly invalid: string | null;
  readonly rules: readonly RuleRow[];
}

function invalidText(placement: PlacementV1): string | null {
  for (const point of [placement.start, placement.end]) {
    if (point !== null && point.ticks.status === 'invalid') {
      return `${INVALID_POINT} ${reasonText(point.ticks.reason)}`;
    }
  }
  return null;
}

/**
 * The page's dates in `orderKey`, id order, each with its rules from its own side. `docs` maps a
 * placement of another page to that page's id, so the rule can open it.
 */
export function datedRows(
  placements: readonly PlacementV1[],
  titles: ReadonlyMap<string, string>,
  calendar: CompiledCalendar | null,
  docs: ReadonlyMap<string, string> = new Map(),
): DateRow[] {
  const own = new Set(placements.map((p) => p.id));
  return [...placements].sort(byOrder).map((p) => ({
    id: p.id,
    placement: p,
    role: roleWord(p.role),
    label: p.label,
    badge: p.effectiveStatus === 'canon' ? null : statusWord(p.effectiveStatus),
    date: placementDate(calendar, p),
    unplaced: p.chronRank === null,
    invalid: invalidText(p),
    rules: p.constraints.map((c) => {
      const parts = constraintParts(c, p.id, titles);
      return {
        id: c.id,
        constraint: c,
        text: `${parts.prefix}${parts.title}`,
        prefix: parts.prefix,
        otherTitle: parts.title,
        otherDocId: own.has(parts.otherId) ? null : (docs.get(parts.otherId) ?? null),
        status: constraintStatusText(c),
      };
    }),
  }));
}

/** The placements on the other side of this page's rules that are not this page's own. */
export function foreignPlacementIds(placements: readonly PlacementV1[]): string[] {
  const own = new Set(placements.map((p) => p.id));
  const ids = new Set<string>();
  for (const p of placements) {
    for (const c of p.constraints) {
      for (const id of [c.beforeId, c.afterId]) if (!own.has(id)) ids.add(id);
    }
  }
  return [...ids].sort();
}

/**
 * The titles a rule names: this page's own placements by their label or the page's title, and
 * the others by their page's title (`owners`: placement id to title, from the Chronicle).
 */
export function ruleTitles(
  placements: readonly PlacementV1[],
  pageTitle: string,
  owners: ReadonlyMap<string, string>,
): Map<string, string> {
  const titles = new Map(owners);
  for (const p of placements) titles.set(p.id, p.label ?? pageTitle);
  return titles;
}

// ------------------------------------------------------------------------------ taking part

export interface EventRow {
  readonly id: string;
  readonly docId: string;
  readonly title: string;
  readonly date: string;
  /** The page's role in the event, when the GM gave one. */
  readonly as: string | null;
}

/**
 * The events this page takes part in: other pages' placements naming it as a participant. The
 * page's own placements are left out (a server without `participating` answers those).
 */
export function eventRows(
  placements: readonly PlacementV1[],
  docId: string,
  titles: ReadonlyMap<string, string>,
  calendar: CompiledCalendar | null,
): EventRow[] {
  return placements
    .filter((p) => p.documentId !== docId)
    .map((p) => {
      const role = p.participants.find((x) => x.docId === docId)?.role ?? '';
      return {
        id: p.id,
        docId: p.documentId,
        title: titles.get(p.documentId) ?? 'Untitled',
        date: placementDate(calendar, p),
        as: role === '' || role === 'participant' ? null : role,
      };
    });
}

// ----------------------------------------------------------------------------- dated links

export interface LinkRow {
  readonly id: string;
  readonly otherId: string;
  readonly title: string;
  /** "1030 – around 12 Ember 1042", "From around …, ongoing", "Until …", or the invalid text. */
  readonly text: string;
  readonly invalid: boolean;
}

const spanSide = (span: TickSpanV1, side: 'lo' | 'hi'): number | null =>
  span.empty ? null : span[side];

/** A placement edge's window as "around A" or "around A – B", at day precision. */
function edgeText(link: LinkV1, side: 'from' | 'to', calendar: CompiledCalendar): string | null {
  const fmt = (tick: number | null) => (tick === null ? null : tickDate(calendar, tick, 'day'));
  // A from-edge lies between the possible and the certain start; a to-edge between the certain
  // and the possible end (`hi` exclusive).
  const [a, b] =
    side === 'from'
      ? [spanSide(link.possibleSpan, 'lo'), spanSide(link.certainSpan, 'lo')]
      : [spanSide(link.certainSpan, 'hi'), spanSide(link.possibleSpan, 'hi')];
  const first = fmt(side === 'from' ? a : a === null ? null : a - 1);
  const last = fmt(side === 'from' ? b : b === null ? null : b - 1);
  if (first === null || last === null || first === last) {
    const one = first ?? last;
    return one === null ? null : `around ${one}`;
  }
  return `around ${first} – ${last}`;
}

function boundaryText(
  boundary: LinkBoundaryV1,
  link: LinkV1,
  side: 'from' | 'to',
  calendar: CompiledCalendar,
): string | null {
  if ('timePoint' in boundary) {
    return formatTimePoint(calendar, boundary.timePoint) ?? boundary.timePoint.label;
  }
  return edgeText(link, side, calendar);
}

/** The links with a validity, the other end's title and the window in words. */
export function linkRows(
  links: readonly LinkV1[],
  docId: string,
  titles: ReadonlyMap<string, string>,
  calendar: CompiledCalendar,
): LinkRow[] {
  return links
    .filter((l) => l.validFrom !== null || l.validTo !== null)
    .map((l) => {
      const otherId = l.sourceId === docId ? l.targetId : l.sourceId;
      const title = titles.get(otherId) ?? 'Untitled';
      if (l.spanStatus === 'invalid') {
        return { id: l.id, otherId, title, text: INVALID_SPAN, invalid: true };
      }
      const side = (b: LinkBoundaryV1 | null, which: 'from' | 'to') =>
        b === null ? null : (boundaryText(b, l, which, calendar) ?? UNKNOWN_DATE);
      const from = side(l.validFrom, 'from');
      const to = side(l.validTo, 'to');
      let text: string;
      if (from && to) text = `${from} – ${to}`;
      else if (from) text = l.ongoing ? `From ${from}, ongoing` : `From ${from}`;
      else text = `Until ${to}`;
      return { id: l.id, otherId, title, text, invalid: false };
    });
}

// -------------------------------------------------------------------------------------- age

export interface AgeRange {
  readonly min: number;
  readonly max: number;
}

/** The whole years from `from` to `at` through the calendar's `add`; null before `from`. */
function wholeYears(calendar: CompiledCalendar, from: number, at: number): number | null {
  if (at < from) return null;
  const one = add(calendar, from, 1, 'year');
  if (!one.ok) return 0;
  const length = Math.max(1, one.tick - from);
  let n = Math.max(0, Math.floor((at - from) / length) - 1);
  while (n > 0) {
    const r = add(calendar, from, n, 'year');
    if (r.ok && r.tick <= at) break;
    n--;
  }
  for (;;) {
    const r = add(calendar, from, n + 1, 'year');
    if (!r.ok || r.tick > at) return n;
    n++;
  }
}

const EXACT_PRECISIONS: ReadonlySet<Precision> = new Set(['day', 'hour', 'minute']);

/**
 * An age at `at`: the whole years from the born point, a range when the born point is fuzzy
 * (coarser than a day, or not exact), null when either is missing, invalid, open or `at` comes
 * before the earliest the person was born.
 */
export function ageAt(
  calendar: CompiledCalendar,
  born: DatedPointV1 | null,
  at: number | null,
): AgeRange | null {
  if (born === null || at === null || born.ticks.status !== 'ok') return null;
  const { lo, hi } = born.ticks;
  const exact = EXACT_PRECISIONS.has(born.point.precision) && born.point.qualifier === 'exact';
  const max = wholeYears(calendar, lo, at);
  if (max === null) return null;
  if (exact) return { min: max, max };
  const min = wholeYears(calendar, hi - 1, at) ?? 0;
  return { min: Math.min(min, max), max };
}

/** "32", or "33–35" for a range. */
export function ageText(age: AgeRange): string {
  return age.min === age.max ? `${age.max}` : `${age.min}–${age.max}`;
}

/**
 * The Age section's line: "32 at NOW (14 Ember 1042)", "29 at death (1 Ember 1040)", "NOW isn't
 * set yet." without NOW, or "—" when no age can be worked out. Dates in the tab's one form.
 */
export function ageLine(
  calendar: CompiledCalendar,
  born: PlacementV1,
  died: PlacementV1 | null,
  now: number | null,
): string {
  const at = died !== null ? diedTick(died) : now;
  const age = ageAt(calendar, born.start, at);
  if (age === null) return died === null && now === null ? NOW_UNSET : '—';
  if (died !== null) return ageAtDeathText(ageText(age), placementDate(calendar, died));
  return ageAtNowText(ageText(age), tickDate(calendar, at ?? 0, 'day') ?? '');
}

/** The first live canon placement of a role, by order key. */
export function canonOf(placements: readonly PlacementV1[], role: string): PlacementV1 | null {
  return (
    [...placements]
      .sort(byOrder)
      .find((p) => p.role === role && p.effectiveStatus === 'canon' && p.start !== null) ?? null
  );
}

/** The tick a died placement stands for: the earliest it can be. */
export function diedTick(died: PlacementV1 | null): number | null {
  const ticks = died?.start?.ticks;
  if (ticks === undefined) return null;
  return ticks.status === 'ok' || ticks.status === 'open_hi' ? ticks.lo : null;
}
